import { useQuery } from '@apollo/client'
import React, { createContext, FC } from 'react'
import { getJournalYoutubeVariables } from '../lib/gql'
import {
  JOURNAL_ITEMS,
  JOURNAL_SECTIONS,
  JOURNAL_YOUTUBE_ITEMS,
} from '../queries/journalQuery'
import { SortOrder } from '../__generated__/globalTypes'
import {
  journalItems,
  journalItemsVariables,
  journalItems_elements_edges_node,
} from '../__generated__/journalItems'
import {
  journalSections,
  journalSectionsVariables,
  journalSections_sections_nodes,
} from '../__generated__/journalSections'
import {
  journalYoutubeItems,
  journalYoutubeItemsVariables,
  journalYoutubeItems_elements_nodes,
} from '../__generated__/journalYoutubeItems'

interface IJournalContext {
  topPosts: journalItems_elements_edges_node[]
  feedPosts: journalItems_elements_edges_node[]
  ytItems: journalYoutubeItems_elements_nodes[]
  sections: journalSections_sections_nodes[]
  loadMore: () => Promise<void>
  hasNextPage: boolean
  loading: boolean
}

export const JournalContext = createContext({} as IJournalContext)

const JournalProvider: FC<{ today: string }> = ({ children, today }) => {
  const { data: sectionsData } = useQuery<
    journalSections,
    journalSectionsVariables
  >(JOURNAL_SECTIONS, {
    variables: {
      where: {
        iblock: { code: { equals: 'articles' } },
        active: { equals: true },
      },
      first: 999,
      order: { sort: SortOrder.asc },
    },
  })
  const {
    data: feed,
    fetchMore,
    loading,
  } = useQuery<journalItems, journalItemsVariables>(JOURNAL_ITEMS, {
    variables: {
      where: {
        iblock: { code: { equals: 'articles' } },
        active: { equals: true },
        OR: [
          {
            activeFrom: { lte: today },
            activeTo: { equals: null },
          },
          {
            activeFrom: { lte: today },
            activeTo: { gte: today },
          },
          {
            activeFrom: { equals: null },
            activeTo: { equals: null },
          },
          {
            activeFrom: { equals: null },
            activeTo: { gte: today },
          },
        ],
      },
      first: 9,
      order: { activeFrom: SortOrder.desc },
    },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = feed?.elements?.pageInfo?.hasNextPage
  const cursor = feed?.elements?.pageInfo?.endCursor

  const loadMore = async () => {
    if (!hasNextPage || !cursor) {
      return null
    }

    await fetchMore({
      variables: {
        where: {
          iblock: { code: { equals: 'articles' } },
          active: { equals: true },
          OR: [
            {
              activeFrom: { lte: today },
              activeTo: { equals: null },
            },
            {
              activeFrom: { lte: today },
              activeTo: { gte: today },
            },
            {
              activeFrom: { equals: null },
              activeTo: { equals: null },
            },
            {
              activeFrom: { equals: null },
              activeTo: { gte: today },
            },
          ],
        },
        first: 6,
        order: { activeFrom: SortOrder.desc },
        after: cursor,
      },
    })
  }

  const { data: ytData } = useQuery<
    journalYoutubeItems,
    journalYoutubeItemsVariables
  >(JOURNAL_YOUTUBE_ITEMS, {
    variables: getJournalYoutubeVariables(),
  })

  const topPosts: journalItems_elements_edges_node[] = []
  topPosts.push(
    feed?.elements?.edges[0]?.node,
    feed?.elements?.edges[1]?.node,
    feed?.elements?.edges[2]?.node,
  )

  const feedPosts = feed?.elements?.edges?.slice(3)?.map((edge) => edge.node)
  const ytItems = ytData?.elements?.nodes
  const sections = sectionsData?.sections?.nodes?.filter(
    (section) => section.elements?.length > 0,
  )

  return (
    <JournalContext.Provider
      value={{
        topPosts,
        feedPosts,
        ytItems,
        sections,
        loadMore,
        hasNextPage,
        loading,
      }}
    >
      {children}
    </JournalContext.Provider>
  )
}
export default JournalProvider
